import { useApiMutation, useApiQuery } from '../../../../hooks/useApiOperations';
import { get, post } from '../../../../utils/api';
import { useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useAlert } from '../../../../contexts/AlertContext';

/**
 * Hook to fetch organizations list with infinite scroll
 * @param {string} text_query - Search query for name or email
 * @param {number} page_size - Number of items per page
 */
export const useOrganisations = (text_query = '', page_size = 10) => {
  return useInfiniteQuery({
    queryKey: ['organisations', text_query, page_size],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await get(`/admin/organisations?page=${pageParam}&page_size=${page_size}&text_query=${encodeURIComponent(text_query)}`);

      // Transform response to standardized structure
      if (response && typeof response === 'object' && 'data' in response && 'pagination' in response) {
        return {
          data: response.data || [],
          pagination: response.pagination || { lastPage: 1 },
          currentPage: pageParam,
        };
      }

      // Fallback for non-paginated responses (backward compatibility)
      return {
        data: Array.isArray(response) ? response : [],
        pagination: { lastPage: 1 },
        currentPage: pageParam,
      };
    },
    getNextPageParam: (lastPage) => {
      const { pagination, currentPage } = lastPage;
      return currentPage < pagination.lastPage ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    initialPageParam: 1,
    enabled: true,
  });
};

/**
 * Hook to create a new organization
 */
export const useCreateOrganisation = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useApiMutation(
    (data) => post('/admin/organisations', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['organisations'] });
        setAlert('Organization created successfully', 'success');
      },
      onError: (error) => {
        const errorMessage = error?.message || 'Failed to create organization';
        setAlert(errorMessage, 'error');
      },
    }
  );
};

/**
 * QC-2927: how many organisations are waiting on Ops to mark their terms
 * signed — the badge on the admin nav's Organisation entry. Lives under the
 * `organisations` key so marking terms signed (below) refreshes it with the
 * list. Polled while the tab is open so a new signup shows up unprompted.
 */
export const usePendingOrganisationsCount = ({ enabled = true } = {}) =>
  useApiQuery(
    ['organisations', 'pending-count'],
    async () => {
      const response = await get('/admin/provider-signup/pending/count');
      return Number(response?.count ?? 0);
    },
    { enabled, staleTime: 60 * 1000, refetchInterval: 5 * 60 * 1000, refetchOnWindowFocus: true }
  );

/**
 * QC-2927: Ops confirm a self-signed-up organisation's terms. Flips it from
 * `pending_terms` to `active` and emails the owner(s); no Stripe customer is
 * created. Optional `invoice_prefix` overrides the derived one.
 */
export const useMarkTermsSigned = (orgId) => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  // useApiMutation already toasts the API's error message; only success copy here.
  return useApiMutation(
    (data = {}) => post(`/admin/provider-signup/organisations/${orgId}/mark-terms-signed`, data),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ['organisations'] });
        const owners = result?.owners_notified ?? 0;
        setAlert(
          `Terms marked signed — organisation is active${owners ? `, ${owners} owner${owners === 1 ? '' : 's'} emailed` : ''}`,
          'success'
        );
      },
    }
  );
};
